import React, { useState, useEffect } from "react";
import {
    TextField, Button, Typography, Box, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent
} from "@mui/material";
import { AddCircle as AddCircleIcon, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";

const PoolManagement = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [salesPersons, setSalesPersons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [newEntry, setNewEntry] = useState({ type: "", name: "", phone: "", email: "" });
    const [isSupplier, setIsSupplier] = useState(true); // Track which table is being edited

    useEffect(() => {
        fetchSuppliers();
        fetchSalesPersons();
    }, []);

    const fetchSuppliers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/suppliers`);
            if (!response.ok) throw new Error("Failed to fetch suppliers.");
            const data = await response.json();
            setSuppliers(data);
        } catch (err) {
            console.error("❌ Error fetching suppliers:", err);
            setError(err.message);
        }
    };

    const fetchSalesPersons = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/sales_persons`);
            if (!response.ok) throw new Error("Failed to fetch sales persons.");
            const data = await response.json();
            setSalesPersons(data);
        } catch (err) {
            console.error("❌ Error fetching sales persons:", err);
            setError(err.message);
        }
    };

    const handleOpenDialog = (isSupplierTable) => {
        setIsSupplier(isSupplierTable);
        setNewEntry({ type: "", name: "", phone: "", email: "" });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSaveNewEntry = async () => {
        try {
            if (!newEntry.name || !newEntry.phone) {
                alert("Name and phone are required.");
                return;
            }

            const endpoint = isSupplier ? "suppliers" : "sales_persons";
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/${endpoint}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    type: newEntry.type || "", // Ensure type is always defined
                    name: newEntry.name,
                    phone: newEntry.phone,
                    email: newEntry.email || "", // Ensure email is always defined
                }),
            });

            if (!response.ok) throw new Error(`Failed to add ${isSupplier ? "supplier" : "sales person"}.`);

            handleCloseDialog();
            isSupplier ? fetchSuppliers() : fetchSalesPersons();
        } catch (error) {
            console.error("❌ Error adding entry:", error);
        }
    };

    const handleDeleteRow = async (id, isSupplierTable) => {
        const endpoint = isSupplierTable ? "suppliers" : "sales_persons";

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/${endpoint}/${id}`, { method: "DELETE" });

            if (!response.ok) throw new Error(`Failed to delete ${isSupplierTable ? "supplier" : "sales person"}.`);

            isSupplierTable ? fetchSuppliers() : fetchSalesPersons();
        } catch (error) {
            console.error(`❌ Error deleting ${isSupplierTable ? "supplier" : "sales person"}:`, error);
        }
    };

    return (
        <Box sx={{ maxWidth: "90vw", mx: "auto", textAlign: "center", minHeight: "100vh", bgcolor: "#121212", color: "#fff", paddingBottom: "50px" }}>
            {loading && <CircularProgress sx={{ marginTop: 2 }} />}
            {error && <Typography color="error" sx={{ marginTop: 2 }}>{error}</Typography>}

            {/* SIDE-BY-SIDE TABLES CONTAINER */}
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 4, marginTop: 3 }}>

                {/* SUPPLIERS TABLE */}
                <TableContainer component={Paper} sx={{ width: "48%", bgcolor: "#1e1e1e", padding: 2 }}>
                    <Typography variant="h6">Suppliers</Typography>
                    <IconButton onClick={() => handleOpenDialog(true)} sx={{ color: "#90caf9" }}>
                        <AddCircleIcon />
                    </IconButton>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {suppliers.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.phone}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            component="a"
                                            href={`https://wa.me/${row.phone.replace(/\D/g, "")}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: "#25D366" }}  // WhatsApp green
                                        >
                                            <WhatsAppIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteRow(row.id, true)} color="error">
                                            ❌
                                        </IconButton>

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* SALES PERSONS TABLE */}
                <TableContainer component={Paper} sx={{ width: "48%", bgcolor: "#1e1e1e", padding: 2 }}>
                    <Typography variant="h6">Sales Persons</Typography>
                    <IconButton onClick={() => handleOpenDialog(false)} sx={{ color: "#90caf9" }}>
                        <AddCircleIcon />
                    </IconButton>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {salesPersons.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.phone}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            component="a"
                                            href={`https://wa.me/${row.phone.replace(/\D/g, "")}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: "#25D366" }}
                                        >
                                            <WhatsAppIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteRow(row.id, false)} color="error">
                                            ❌
                                        </IconButton>

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

            {/* ADD SUPPLIER / SALES PERSON DIALOG */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogContent>
                    <Typography>{isSupplier ? "Add Supplier" : "Add Sales Person"}</Typography>
                    {isSupplier && (
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Type"
                            fullWidth
                            value={newEntry.type}
                            onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
                        />
                    )}
                    <TextField
                        margin="dense"
                        label="Name"
                        fullWidth
                        required
                        value={newEntry.name}
                        onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Phone"
                        fullWidth
                        required
                        value={newEntry.phone}
                        onChange={(e) => setNewEntry({ ...newEntry, phone: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Email"
                        fullWidth
                        value={newEntry.email}
                        onChange={(e) => setNewEntry({ ...newEntry, email: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <IconButton onClick={handleSaveNewEntry} color="success"><CheckIcon /></IconButton>
                    <IconButton onClick={handleCloseDialog} color="error"><CloseIcon /></IconButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PoolManagement;
